import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { EnergyEfficiencySettingService, RealEstateSetting, TenantUnitSetting } from '@eadsysteme/energyefficiencyclient-v1';
import { Owner } from '@eadsysteme/ewsrealestateclient-v2';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { SessionService } from 'src/app/services/session.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { Icons } from 'src/app/util/Icons';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { LayoutService } from '../../../../@vex/services/layout.service';
import { PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { RealEstate, TenantUnit } from '@eadsysteme/ewsrealestateclient-v1';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import ManualDeliveryOptionEnum = RealEstateSetting.ManualDeliveryOptionEnum;
import PermissionTypeEnum = PermissionObject.PermissionTypeEnum;

@Component({
	selector: 'ara-tenant-unit-detail-settings-legacy',
	templateUrl: './tenant-unit-detail-settings-legacy.component.html',
	styleUrls: ['./tenant-unit-detail-settings-legacy.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		TranslocoDirective,
		MatDialogTitle,
		CdkDrag,
		CdkDragHandle,
		CdkScrollable,
		MatDialogContent,
		MatFormField,
		MatLabel,
		MatSelect,
		MatOption,
		MatDialogActions,
		MatButton,
		MatIcon,
	],
})
export class TenantUnitDetailSettingsLegacyComponent implements OnInit {
	private translocoService = inject(TranslocoService);
	data = inject(MAT_DIALOG_DATA);
	dialogRef = inject<MatDialogRef<TenantUnitDetailSettingsLegacyComponent>>(MatDialogRef);
	sessionService = inject(SessionService);
	private energyEfficiencySettingService = inject(EnergyEfficiencySettingService);
	private snackbar = inject(SnackbarService);
	dialog = inject(MatDialog);
	icons = inject(Icons);
	private layoutService = inject(LayoutService);

	private destroyRef = inject(DestroyRef);

	public manualDeliveryOptions: string[] = Object.values(RealEstateSetting.ManualDeliveryOptionEnum);
	public manualDeliveryOption: any = {};
	public ownerHasInvalidAddress: boolean;

	isMobile = false;
	currentUserIsOwner = false;
	currentUserIsPM = false;
	showManualDeliveryOptionSetting = false;
	private currentTenantUnitSetting: TenantUnitSetting;
	private realEstateSetting: RealEstateSetting;
	private currentArea: number;
	tenantUnit: WritableSignal<TenantUnit> = signal(undefined);
	realEstate: WritableSignal<RealEstate> = signal(undefined);

	constructor() {
		const data = this.data;
		const layoutService = this.layoutService;

		this.currentArea = Number(sessionStorage.getItem('selectedRealEstateArea') ?? this.sessionService.effectiveArea);
		this.isMobile = layoutService.isMobile();
		this.manualDeliveryOptions = this.manualDeliveryOptions.filter((value) => value !== RealEstateSetting.ManualDeliveryOptionEnum.UNKNOWN);
		this.manualDeliveryOptions.unshift('PARENT_VALUE');

		this.tenantUnit.set(data.currentTenantUnit);
		this.realEstate.set(data.currentRealEstate);
	}

	ngOnInit(): void {
		this.loadTenantUnitSetting();
	}

	loadTenantUnitSetting() {
		if (!this.tenantUnit().id?.length) {
			return;
		}
		this.energyEfficiencySettingService
			.getTenantUnitSetting(this.currentArea, this.tenantUnit().id)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((tenantUnitSetting) => {
				this.currentTenantUnitSetting = tenantUnitSetting;
				this.processTenantUnitSetting();
			});
	}

	processTenantUnitSetting() {
		if (this.currentTenantUnitSetting.manualDeliveryOption) {
			this.manualDeliveryOption = this.currentTenantUnitSetting.manualDeliveryOption;
		} else {
			this.manualDeliveryOption = 'PARENT_VALUE';
		}
		this.loadOwnerInfo();
	}

	close(saveSettings: boolean) {
		if (saveSettings) {
			if (this.ownerHasInvalidAddress) {
				if (this.manualDeliveryOption === ManualDeliveryOptionEnum.DELIVERY_TO_OWNER) {
					const warnText = this.translocoService.translate('tenantunitDetails.ownerHasNoValidAddress');
					return this.showOwnerWithoutAddressError(warnText);
				}
				if (
					(!this.manualDeliveryOption || this.manualDeliveryOption === 'PARENT_VALUE') &&
					this.realEstateSetting.manualDeliveryOption === ManualDeliveryOptionEnum.DELIVERY_TO_OWNER
				) {
					const warnText = this.translocoService.translate('tenantunitDetails.ownerHasNoValidAddressRealEstateSettingDeliveryToOwner');
					return this.showOwnerWithoutAddressError(warnText);
				}
			}
			this.saveSettings();
			this.dialog.closeAll();
		} else {
			this.dialog.closeAll();
		}
	}

	loadOwnerInfo() {
		if (!this.currentTenantUnitSetting) {
			return;
		}
		this.ownerHasInvalidAddress = !this.validateAddress(this.tenantUnit().currentOwner);

		this.sessionService
			.getAllPermissionsOfLoggedInUser()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((permissions) => {
				const permissionTypesMap = permissions.map((value, index) => value.permissionType);
				if (permissionTypesMap.includes(PermissionTypeEnum.PROPERTY_MANAGEMENT)) {
					this.currentUserIsPM = true;
				} else if (permissionTypesMap.includes(PermissionTypeEnum.OWNER)) {
					if (permissions.filter((value) => value.tenantUnitId === this.tenantUnit().id).length) {
						this.currentUserIsOwner = true;
					}
				}
				this.energyEfficiencySettingService
					.getRealEstateSetting(this.sessionService.effectiveArea, this.realEstate().id)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe((realEstateSetting) => {
						this.realEstateSetting = realEstateSetting;
						this.showManualDeliveryOptionSetting = this.manualDeliveryOptionAllowedBySetting();
					});
			});
	}

	showOwnerWithoutAddressError(warnText: string) {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.titleMessage = this.translocoService.translate('errors.warning');
		dialogRef.componentInstance.confirmMessage = warnText;
		dialogRef.componentInstance.showCloseButton = true;
		dialogRef.componentInstance.showConfirmButton = false;
		dialogRef.componentInstance.closeButtonText = this.translocoService.translate('common.ok');
	}

	manualDeliveryOptionAllowedBySetting() {
		if (
			(this.realEstateSetting?.pmAllowedManualDeliveryOption && this.currentUserIsPM) ||
			(this.realEstateSetting?.ownerAllowedManualDeliveryOption && this.currentUserIsOwner)
		) {
			return true;
		}
		return false;
	}

	private validateAddress(currentOwner: Owner) {
		return (
			currentOwner &&
			currentOwner.address &&
			currentOwner.address?.streets.at(0)?.streetName &&
			currentOwner.address?.streets.at(0)?.houseNumber &&
			currentOwner.address?.zipCode &&
			currentOwner.address?.city
		);
	}

	private saveSettings() {
		const tenantUnitSetting: TenantUnitSetting = {};
		tenantUnitSetting.tenantUnitId = this.tenantUnit().id;
		tenantUnitSetting.manualDeliveryOption = this.manualDeliveryOption === 'PARENT_VALUE' ? null : this.manualDeliveryOption;
		this.energyEfficiencySettingService
			.createOrUpdateTenantUnitSetting(this.currentArea, this.tenantUnit().id, tenantUnitSetting)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.snackbar.success(this.translocoService.translate('realestateProperties.propertiesSaved'));
				},
				error: () => {
					this.snackbar.error(this.translocoService.translate('errors.propertiesNotSaved'));
				},
			});
	}
}
